// React components built on top of MUI (MaterialUI).

import React from "react"
import { useNavigate } from "react-router-dom"
import {
  CssBaseline,
  Fab,
  SvgIcon,
  ThemeProvider,
  colors,
  createTheme,
  styled,
} from "@mui/material"
import COLORS from './colors'

// Icons
const ArrowBackIcon = (props) => (
  <SvgIcon {...props}>
    <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
  </SvgIcon>
)

const BuyIcon = (props) => (
  <SvgIcon {...props}>
    <path d="M19 6H17C17 3.2 14.8 1 12 1S7 3.2 7 6H5C3.9 6 3 6.9 3 8V20C3 21.1 3.9 22 5 22H19C20.1 22 21 21.1 21 20V8C21 6.9 20.1 6 19 6M12 3C13.7 3 15 4.3 15 6H9C9 4.3 10.3 3 12 3M19 20H5V8H19V20M12 12C10.3 12 9 10.7 9 9H7C7 11.8 9.2 14 12 14S17 11.8 17 9H15C15 10.7 13.7 12 12 12Z" />
  </SvgIcon>
)

const ClaimIcon = (props) => (
  <SvgIcon {...props}>
    <path d="M16.5 12C16.5 11.32 16.44 10.66 16.36 10H19.74C19.9 10.64 20 11.31 20 12C20 12.37 19.97 12.73 19.92 13.08C20.61 13.18 21.25 13.4 21.84 13.72C21.94 13.16 22 12.59 22 12C22 6.5 17.5 2 12 2C6.47 2 2 6.5 2 12C2 17.5 6.5 22 12 22C12.59 22 13.16 21.94 13.72 21.84C13.26 21 13 20.03 13 19C13 18.71 13.03 18.43 13.07 18.15C12.75 18.78 12.4 19.39 12 19.96C11.17 18.76 10.5 17.43 10.09 16H13.81C14.41 14.96 15.31 14.12 16.4 13.6C16.46 13.07 16.5 12.54 16.5 12M12 4.03C12.83 5.23 13.5 6.57 13.91 8H10.09C10.5 6.57 11.17 5.23 12 4.03M4.26 14C4.1 13.36 4 12.69 4 12S4.1 10.64 4.26 10H7.64C7.56 10.66 7.5 11.32 7.5 12S7.56 13.34 7.64 14H4.26M5.08 16H8C8.35 17.25 8.8 18.45 9.4 19.56C7.57 18.93 6.03 17.65 5.08 16M8 8H5.08C6.03 6.34 7.57 5.06 9.4 4.44C8.8 5.55 8.35 6.75 8 8M14.34 14H9.66C9.56 13.34 9.5 12.68 9.5 12S9.56 10.65 9.66 10H14.34C14.43 10.65 14.5 11.32 14.5 12S14.43 13.34 14.34 14M14.59 4.44C16.43 5.07 17.96 6.34 18.92 8H15.97C15.65 6.75 15.19 5.55 14.59 4.44M23 18V20H20V23H18V20H15V18H18V15H20V18H23Z" />
  </SvgIcon>
)

// Styled components replacing makeStyles
const StyledBackFab = styled(Fab)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(2),
  left: theme.spacing(2),
  zIndex: 2,
  "&:hover": {
    backgroundColor: "inherit",
  },
  "&:active": {
    backgroundColor: "inherit",
  },
}))

const StyledBuyFab = styled(Fab)(({ theme, bgColor = COLORS.black }) => ({
  position: "absolute",
  bottom: theme.spacing(3),
  right: theme.spacing(3),
  zIndex: 2,
  backgroundColor: bgColor,
  "&:hover": {
    backgroundColor: bgColor,
  },
  "&:active": {
    backgroundColor: bgColor,
  },
}))

const StyledClaimFab = styled(Fab)(({ theme, bgColor = COLORS.black }) => ({
  position: "absolute",
  bottom: theme.spacing(3),
  left: theme.spacing(3),
  zIndex: 2,
  backgroundColor: bgColor,
  "&:hover": {
    backgroundColor: bgColor,
  },
  "&:active": {
    backgroundColor: bgColor,
  },
}))

// Buttons
const FloatingBackButton = () => {
  const navigate = useNavigate()
  return (
    <StyledBackFab
      color="secondary"
      onClick={() => navigate(-1)}
    >
      <ArrowBackIcon style={{ fontSize: 20 }} />
    </StyledBackFab>
  )
}

const BuyButton = ({ 
  bgColor, 
  iconColor = COLORS.white,
  url = "https://www.arkivist.xyz/" // default URL
}) => {
  const [isVisible, setIsVisible] = React.useState(false)

  React.useEffect(() => {
    const handleInstructionsFinished = () => {
      setIsVisible(true)
    }

    // Add event listener
    window.addEventListener('instructionsFinished', handleInstructionsFinished)

    // Cleanup
    return () => {
      window.removeEventListener('instructionsFinished', handleInstructionsFinished)
    }
  }, [])

  if (!isVisible) return null

  return (
    <StyledBuyFab
      bgColor={bgColor}
      onClick={() => window.open(url)}
    >
      <BuyIcon style={{ fontSize: 28, color: iconColor }} />
    </StyledBuyFab>
  )
}

const ClaimButton = ({ 
  bgColor, 
  iconColor = COLORS.white,
  url = "https://www.arkivist.xyz/" // default URL
}) => {
  const [isVisible, setIsVisible] = React.useState(false)

  React.useEffect(() => {
    const handleInstructionsFinished = () => {
      setIsVisible(true)
    }

    // Add event listener
    window.addEventListener('instructionsFinished', handleInstructionsFinished)

    // Cleanup
    return () => {
      window.removeEventListener('instructionsFinished', handleInstructionsFinished)
    }
  }, [])

  if (!isVisible) return null

  return (
    <StyledClaimFab
      bgColor={bgColor}
      onClick={() => window.open(url)}
    >
      <ClaimIcon style={{ fontSize: 28, color: iconColor}} />
    </StyledClaimFab>
  )
}

// Theme
const theme = createTheme({
  palette: {
    primary: {
      main: COLORS.black,
    },
    secondary: {
      main: COLORS.white,
    },
    error: {
      main: colors.red.A400,
    },
    background: {
      default: "#fff",
    },
  },
  typography: {
    fontFamily: ["Nunito", "Roboto", "Arial", "sans-serif"].join(","),
  },
})

const MaterialUIApp = (props) => (
  <ThemeProvider theme={theme}>
    <CssBaseline />
    {props.children}
  </ThemeProvider>
)

export { FloatingBackButton, BuyButton, MaterialUIApp, ClaimButton }
